import { inject, Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { WebSocketService } from '../../services/gateway.service';
import {
  BoardColumnsUpdatedEvent,
  BoardFieldsUpdatedEvent,
  CardCreatedEvent,
  CardDeletedEvent,
  CardDetailUpdatedEvent,
  CardTimelineSocketEvent,
  CardPresenceEvent,
  CardUpdatedEvent,
  ChatMessageNewEvent,
  ExportProgressEvent,
  FunnelCreatedEvent,
  FunnelDeletedEvent,
  FunnelPresenceEvent,
  FunnelUpdatedEvent,
  ImportProgressEvent,
  LeadConversionResultEvent,
} from './board-v3.types';
import { FUNNEL_TYPE } from '../../enum/constants';
import { ChatTimelineSocket } from '../../interfaces/timeline.interface';

interface CardRoomSubscription {
  cardId: string;
  companyId: string;
  funnelId?: string;
  references: number;
  viewers: number;
}

@Injectable({ providedIn: 'root' })
export class BoardV3RealtimeService {
  private readonly ws = inject(WebSocketService);
  private readonly zone = inject(NgZone);
  private readonly funnelSubscriptions = new Set<string>();
  private readonly cardSubscriptions = new Map<string, CardRoomSubscription>();

  constructor() {
    this.ws.connected$().subscribe(() => this.resubscribeActiveRooms());
  }

  subscribe(funnelId: string): void {
    this.funnelSubscriptions.add(funnelId);
    this.ws.subscribeFunnel(funnelId);
  }

  unsubscribe(funnelId: string): void {
    this.funnelSubscriptions.delete(funnelId);
    this.ws.unsubscribeFunnel(funnelId);
  }

  subscribeCard(cardId: string, companyId: string, funnelId?: string, observeOnly = false): void {
    const key = this.cardSubscriptionKey(cardId, companyId, funnelId);
    const existing = this.cardSubscriptions.get(key);
    if (existing) {
      existing.references++;
      if (!observeOnly && existing.viewers++ === 0) this.ws.subscribeCard(cardId, companyId, funnelId);
      return;
    }
    this.cardSubscriptions.set(key, {
      cardId,
      companyId,
      funnelId,
      references: 1,
      viewers: observeOnly ? 0 : 1,
    });
    this.ws.subscribeCard(cardId, companyId, funnelId, observeOnly);
  }

  unsubscribeCard(cardId: string, companyId: string, funnelId?: string, observeOnly = false): void {
    const key = this.cardSubscriptionKey(cardId, companyId, funnelId);
    const existing = this.cardSubscriptions.get(key);
    if (!existing) return;
    if (!observeOnly) existing.viewers--;
    if (--existing.references > 0) {
      if (!observeOnly && existing.viewers === 0) this.ws.unsubscribeCard(cardId, companyId, funnelId, true);
      return;
    }
    this.cardSubscriptions.delete(key);
    this.ws.unsubscribeCard(cardId, companyId, funnelId);
  }

  pingFunnelPresence(funnelId: string): void {
    this.ws.pingFunnelPresence(funnelId);
  }

  pingCardPresence(cardId: string, companyId: string, funnelId?: string): void {
    this.ws.pingCardPresence(cardId, companyId, funnelId);
  }

  cardCreated$(funnelId: string): Observable<CardCreatedEvent> {
    return this.ws.cardCreated().pipe(
      map(d => d as CardCreatedEvent),
      filter(d => d.card?.funnel_id === funnelId),
      this.inZone(),
    );
  }

  cardUpdated$(funnelId: string): Observable<CardUpdatedEvent> {
    return this.ws.boardCardUpdated().pipe(
      map(d => d as CardUpdatedEvent),
      filter(d => d.card?.funnel_id === funnelId),
      this.inZone(),
    );
  }

  cardDetailUpdated$(cardId: string): Observable<CardDetailUpdatedEvent> {
    return this.ws.cardDetailUpdated().pipe(
      map(d => d as CardDetailUpdatedEvent),
      filter(d => d.card?.id === cardId),
      this.inZone(),
    );
  }

  /** Timeline socket of a card, contact, company or task; the client gets it while it is in the entity room */
  timelineEvent$(entityType: string, entityId: string): Observable<CardTimelineSocketEvent> {
    return this.ws.timelineEvent().pipe(
      map(d => d as CardTimelineSocketEvent),
      filter(d => d?.entity_type === entityType && d.entity_id === entityId && !!d.timelines?.length),
      this.inZone(),
    );
  }

  /** Chats have a socket of their own, sent to everyone who can open the chat */
  chatTimelineEvent$(chatId: string): Observable<ChatTimelineSocket> {
    return this.ws.chatTimelineEvents().pipe(
      filter(d => d?.chat_id === chatId && !!d.events?.length),
      this.inZone(),
    );
  }

  cardDeleted$(funnelId: string): Observable<CardDeletedEvent> {
    return this.ws.cardDeleted().pipe(
      map(d => d as CardDeletedEvent),
      filter(d => !d.funnel_id || d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  chatMessageNew$(): Observable<ChatMessageNewEvent> {
    return this.ws.getNewMessage().pipe(
      map(d => d as ChatMessageNewEvent),
      filter(d => !!d?.chat?.id),
      this.inZone(),
    );
  }

  columnsUpdated$(funnelId: string): Observable<BoardColumnsUpdatedEvent> {
    return this.ws.boardColumnsUpdated().pipe(
      map(d => d as BoardColumnsUpdatedEvent),
      filter(d => d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  fieldsUpdated$(funnelId: string): Observable<BoardFieldsUpdatedEvent> {
    return this.ws.boardFieldsUpdated().pipe(
      map(d => d as BoardFieldsUpdatedEvent),
      filter(d => d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  funnelCreated$(type: FUNNEL_TYPE): Observable<FunnelCreatedEvent> {
    return this.ws.funnelCreated().pipe(
      map(d => d as FunnelCreatedEvent),
      filter(d => this.isFunnelEventForType(d, type)),
      this.inZone(),
    );
  }

  funnelUpdated$(type: FUNNEL_TYPE): Observable<FunnelUpdatedEvent> {
    return this.ws.funnelUpdated().pipe(
      map(d => d as FunnelUpdatedEvent),
      filter(d => this.isFunnelEventForType(d, type)),
      this.inZone(),
    );
  }

  funnelUpdatedById$(funnelId: string): Observable<FunnelUpdatedEvent> {
    return this.ws.funnelUpdated().pipe(
      map(d => d as FunnelUpdatedEvent),
      filter(d => d.funnel_id === funnelId || d.funnel?.id === funnelId),
      this.inZone(),
    );
  }

  funnelDeleted$(type: FUNNEL_TYPE): Observable<FunnelDeletedEvent> {
    return this.ws.funnelDeleted().pipe(
      map(d => d as FunnelDeletedEvent),
      filter(d => this.isFunnelEventForType(d, type)),
      this.inZone(),
    );
  }

  cardPresence$(funnelId: string): Observable<CardPresenceEvent> {
    return this.ws.cardPresence().pipe(
      map(d => d as CardPresenceEvent),
      filter(d => !d.funnel_id || d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  funnelPresence$(funnelId: string): Observable<FunnelPresenceEvent> {
    return this.ws.funnelPresence().pipe(
      map(d => d as FunnelPresenceEvent),
      filter(d => d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  importProgress$(funnelId: string): Observable<ImportProgressEvent> {
    return this.ws.importProgress().pipe(
      map(d => d as ImportProgressEvent),
      filter(d => !d.funnel_id || d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  exportProgress$(funnelId: string): Observable<ExportProgressEvent> {
    return this.ws.exportProgress().pipe(
      map(d => d as ExportProgressEvent),
      filter(d => !d.funnel_id || d.funnel_id === funnelId),
      this.inZone(),
    );
  }

  leadConversionResult$(funnelId: string): Observable<LeadConversionResultEvent> {
    return this.ws.leadConversionResult().pipe(
      map(d => d as LeadConversionResultEvent),
      filter(d => d.lead_funnel_id === funnelId),
      this.inZone(),
    );
  }

  connectionRestored$(): Observable<void> {
    return this.ws.connectionRestored$().pipe(this.inZone());
  }

  private resubscribeActiveRooms(): void {
    this.funnelSubscriptions.forEach(funnelId => this.ws.subscribeFunnel(funnelId));
    this.cardSubscriptions.forEach(({ cardId, companyId, funnelId, viewers }) => {
      this.ws.subscribeCard(cardId, companyId, funnelId, viewers === 0);
    });
  }

  private cardSubscriptionKey(cardId: string, companyId: string, funnelId?: string): string {
    return [cardId, companyId, funnelId ?? ''].join(':');
  }

  private isFunnelEventForType(
    event: { funnel?: { type?: FUNNEL_TYPE }; type?: FUNNEL_TYPE },
    type: FUNNEL_TYPE,
  ): boolean {
    return (event.funnel?.type ?? event.type) === type;
  }

  private inZone<T>() {
    return (source: Observable<T>): Observable<T> =>
      new Observable<T>(observer =>
        source.subscribe({
          next: v => this.zone.run(() => observer.next(v)),
          error: e => this.zone.run(() => observer.error(e)),
          complete: () => this.zone.run(() => observer.complete()),
        }),
      );
  }
}
